import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { AgendaDataService } from '@nomoh-monorepo/data-access-bardo-administrador';
import { I18nService } from '@nomoh-monorepo/data-access-shared-i18n';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { MultiSelectModule } from 'primeng/multiselect';

@Component({
  selector: 'lib-agenda-filtro-calendario-cabecalho',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MultiSelectModule,
    CommonModule,
    FormsModule,
    ButtonModule,
    TranslateModule,
  ],
  templateUrl: './agenda-filtro-calendario-cabecalho.component.html',
  styleUrls: ['./agenda-filtro-calendario-cabecalho.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgendaFiltroCalendarioCabecalhoComponent implements OnInit {
  private calendar = inject(MatCalendar<Date>);
  private adapter = inject(DateAdapter<Date>);
  // TODO: Descomentar quando AgendaFiltroService estiver disponível
  // agendaFiltroService = inject(AgendaFiltroService);
  AgendaDataService = inject(AgendaDataService);
  private i18nService = inject(I18nService);

  // 🔁 Signal que espelha mudanças do calendar
  $activeDate = signal(this.calendar.activeDate);
  $currentView = signal(this.calendar.currentView);
  $periodLabel = signal(this.montarPeriodoLabel());

  $iconVisualizarDiasConfirmados = signal('pi pi-eye');

  constructor() {
    effect(
      () => {
        const visualizar = this.AgendaDataService.$visualizarDiasConfirmados();
        this.$iconVisualizarDiasConfirmados.set(
          visualizar ? 'pi pi-eye' : 'pi pi-eye-slash'
        );
      }
    );

    effect(() => {
      this.i18nService.$currentLang();
      this.$periodLabel.set(this.montarPeriodoLabel());
    });
  }

  ngOnInit(): void {
    this.calendar.stateChanges.subscribe(() => {
      queueMicrotask(() => {
        this.$activeDate.set(this.calendar.activeDate);
        this.$currentView.set(this.calendar.currentView);
        this.$periodLabel.set(this.montarPeriodoLabel());
      });
    });
  }

  previous(): void {
    const view = this.calendar.currentView;
    const active = this.calendar.activeDate;

    let newDate: Date;

    if (view === 'month') {
      newDate = this.adapter.addCalendarMonths(active, -1);
    } else if (view === 'year') {
      newDate = this.adapter.addCalendarYears(active, -1);
    } else {
      // 'multi-year' navega em blocos de 24 anos
      newDate = this.adapter.addCalendarYears(active, -24);
    }

    this.calendar.activeDate = newDate;
    this.$activeDate.set(newDate);
    this.$periodLabel.set(this.montarPeriodoLabel());
    this.calendar.stateChanges.next();
  }

  next(): void {
    const view = this.calendar.currentView;
    const active = this.calendar.activeDate;

    let newDate: Date;

    if (view === 'month') {
      newDate = this.adapter.addCalendarMonths(active, 1);
    } else if (view === 'year') {
      newDate = this.adapter.addCalendarYears(active, 1);
    } else {
      // 'multi-year' navega em blocos de 24 anos
      newDate = this.adapter.addCalendarYears(active, 24);
    }

    this.calendar.activeDate = newDate;
    this.$activeDate.set(newDate);
    this.$periodLabel.set(this.montarPeriodoLabel());
    this.calendar.stateChanges.next();
  }

  toggleYearView(): void {
    const next =
      this.calendar.currentView === 'multi-year' ? 'month' : 'multi-year';
    this.calendar.currentView = next;
    this.$currentView.set(next);
    this.$periodLabel.set(this.montarPeriodoLabel());
    this.calendar.stateChanges.next();
  }

  private montarPeriodoLabel(): string {
    const date = this.$activeDate();
    const view = this.$currentView();

    if (view === 'multi-year') {
      const y = this.adapter.getYear(date);
      return `${y - 3} – ${y + 20}`;
    } else if (view === 'year') {
      return this.adapter.getYear(date).toString();
    }
    const locale = this.i18nService?.$locale() || 'pt-BR';
    const labelMes = new Intl.DateTimeFormat(locale, { month: 'long' })
      .format(date)
      .toUpperCase();
    if (locale.startsWith('en')) {
      return `${labelMes} ${this.adapter.getYear(date)}`;
    }
    return `${this.adapter.getYear(date)} ${labelMes}`;
  }

  irParaHoje(): void {
    const hoje = new Date();

    // Define a data ativa do calendário para hoje
    this.calendar.activeDate = hoje;
    this.$activeDate.set(hoje);

    // Se estiver em view de ano ou multi-year, volta para month
    if (this.calendar.currentView !== 'month') {
      this.calendar.currentView = 'month';
      this.$currentView.set('month');
    }

    // Seleciona a data de hoje
    this.calendar._dateSelected({
      value: hoje,
      event: new Event('click'),
    });

    // Atualiza o label do período
    this.$periodLabel.set(this.montarPeriodoLabel());
    this.calendar.stateChanges.next();

    // Comunica a mudança para o DataService
    this.AgendaDataService.setDataSelecionada(hoje);

    // TODO: Descomentar quando AgendaFiltroService estiver disponível
    // this.agendaFiltroService.alterarData(hoje);
  }

  alternarVisualizacao() {
    this.AgendaDataService.setVisualizarDiasConfirmados(
      !this.AgendaDataService.$visualizarDiasConfirmados()
    );
  }
}
